import type { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import type { Price } from '@noths/polaris-dev-ts-types';
import type { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { DeliveryBlockOnCountryChangeArgs } from 'src/components/organisms/DeliverySection/components/DeliveryBlock/types';
import type { DeliverySettingsAPIDeliveryZone } from 'src/services/delivery-settings/types/DeliverySettingsAPIDeliveryZone';
import { logger } from 'src/utils/serverLogger';
import { requestDeliveryZoneOptions, requestPartnerDeliverySettings } from './thunks';

export interface DeliveryState {
  deliveryZoneOptionsLoading: boolean;
  error: SerializedError | null;
  options: DeliverySettingsAPIDeliveryZone[];
  partnerDeliverySettingsLoading: boolean;
  partnerFreeDeliveryThreshold: Price | null;
  previouslySelectedZone: DeliveryZoneCode | null;
  selectedCountry: string;
  selectedZone: DeliveryZoneCode;
}

export const initialState: Readonly<DeliveryState> = {
  deliveryZoneOptionsLoading: true,
  error: null,
  options: [],
  partnerDeliverySettingsLoading: false,
  partnerFreeDeliveryThreshold: null,
  previouslySelectedZone: null,
  selectedZone: 'MAINLAND_UK',
  selectedCountry: 'GB',
};

export const { actions, reducer } = createSlice({
  name: 'delivery',
  initialState,
  reducers: {
    setSelectedZone: (state, { payload }: PayloadAction<DeliveryZoneCode | undefined>) => {
      if (payload) {
        state.previouslySelectedZone = state.selectedZone;
        state.selectedZone = payload;
      }
    },
    setSelectedCountry: (
      state,
      { payload }: PayloadAction<DeliveryBlockOnCountryChangeArgs | string | undefined>,
    ) => {
      if (payload) {
        state.selectedCountry = typeof payload === 'object' ? payload.selectedCountry : payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestDeliveryZoneOptions.fulfilled, (state, { payload }) => {
        state.options = payload;
        state.deliveryZoneOptionsLoading = false;
      })
      .addCase(requestDeliveryZoneOptions.rejected, (state, action) => {
        logger.error('Error requesting delivery zone options', action.error);

        state.error = action.error;
        state.deliveryZoneOptionsLoading = false;
      })
      .addCase(requestPartnerDeliverySettings.pending, (state) => {
        state.partnerDeliverySettingsLoading = true;
      })
      .addCase(requestPartnerDeliverySettings.fulfilled, (state, { payload }) => {
        state.partnerDeliverySettingsLoading = false;

        if (payload.overThirtyFree) {
          state.partnerFreeDeliveryThreshold = {
            amount: 3000,
            currency: 'GBP',
          };
        }
      })
      .addCase(requestPartnerDeliverySettings.rejected, (state, action) => {
        state.partnerDeliverySettingsLoading = false;
        logger.error('Error requesting partner delivery settings', action.error);
      });
  },
});
